import {
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonLabel,
  IonLoading,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
  useIonViewDidEnter
} from '@ionic/react';
import { Buffer } from 'buffer';
import { arrowBack } from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { empresaAlterar, empresaConfiguracoesCarregar, requestService } from '../../../../../Utils/Services';
import { MessageContext } from '../../../../../contexts/MessageContext';
import './Cashback.css';

type Props = {
  abrirMenu: boolean;
};

// Constantes
const MIN_PERCENTUAL = 0;
const MAX_PERCENTUAL = 20;
const MAX_DIGITOS_INTEIROS = 2;

const Cashback: React.FC<Props> = (props: Props) => {
  const history = useHistory();
  const { loja, setEsconderMenu, setNomeMenu } = useContext(MessageContext);
  const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));

  // Estados de UI
  const [showToast, setShowToast] = useState(false);
  const [textToast, setTextToast] = useState("");
  const [showLoading, setShowLoading] = useState(true);
  const [showSucesso, setShowSucesso] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  // Estados de configurações
  const [cashbackAtivado, setCashbackAtivado] = useState(false);
  const [percentualCashback, setPercentualCashback] = useState('');
  const [prazoCashback, setPrazoCashback] = useState(15);

  useIonViewDidEnter(() => {
    setEsconderMenu(true);
    setNomeMenu("");
    carregarConfiguracoes();
  });

  // Funções de formatação e validação
  const formatarPercentual = (valor: string): string => {
    if (!valor || valor === '') return '';

    // Remove TUDO exceto números - apenas inteiros
    let limpo = valor.replace(/\D/g, '');

    return limpo;
  };

  const validarPercentual = (valor: string): boolean => {
    if (!valor || valor === '') return false;

    const numero = parseInt(valor, 10);
    return !isNaN(numero) && numero > MIN_PERCENTUAL && numero <= MAX_PERCENTUAL;
  };

  const handlePercentualChange = (e: any) => {
    let inputValue = e.target.value;

    // Se vazio, limpa
    if (inputValue === '') {
      setPercentualCashback('');
      return;
    }

    // Formata o valor (apenas números inteiros)
    inputValue = formatarPercentual(inputValue);

    // Se ficou vazio após formatação, limpa
    if (inputValue === '') {
      setPercentualCashback('');
      return;
    }

    // Limita a MAX_DIGITOS_INTEIROS
    if (inputValue.length > MAX_DIGITOS_INTEIROS) {
      // Pega apenas os primeiros MAX_DIGITOS_INTEIROS dígitos
      inputValue = inputValue.substring(0, MAX_DIGITOS_INTEIROS);
    }

    // Valida se está dentro do limite e ajusta automaticamente
    const numero = parseInt(inputValue, 10);
    if (!isNaN(numero) && numero > MAX_PERCENTUAL) {
      // Se ultrapassou, ajusta para o máximo permitido
      setPercentualCashback(MAX_PERCENTUAL.toString());
      return;
    }

    setPercentualCashback(inputValue);
  };

  const handlePercentualBlur = () => {
    // Quando sai do campo, simplesmente valida se está vazio e limpa se necessário
    if (percentualCashback && percentualCashback !== '') {
      const numero = parseInt(percentualCashback, 10);
      if (isNaN(numero) || numero <= 0) {
        setPercentualCashback('');
      }
    }
  };

  // Funções de API
  const carregarConfiguracoes = async () => {
    try {
      const id = loja;
      const url = empresaConfiguracoesCarregar.url
        .replace("{id}", id.replaceAll(/#/g, '%23'))
        .concat("?filtro=", Buffer.from(loja).toString("base64"));

      requestService(
        url,
        {
          method: empresaConfiguracoesCarregar.method,
          headers: [["token", window.localStorage.getItem("token")]],
          credentials: 'include'
        },
        history,
        (response: any) => {
          if (response.Item) {
            setCashbackAtivado(response.Item.cashbackAtivado || false);
            const percentual = response.Item.percentualCashback || 5;
            // Arredonda para inteiro ao carregar
            setPercentualCashback(Math.round(parseFloat(percentual.toString())).toString());
            setPrazoCashback(response.Item.prazoCashback || 15);
          }
          setShowLoading(false);
        },
        (error: any) => {
          console.error('Erro ao carregar configurações:', error);
          setShowLoading(false);
          exibirToast("Erro ao carregar configurações.");
        }
      );
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setShowLoading(false);
      exibirToast("Erro ao carregar configurações.");
    }
  };

  const salvarConfiguracoes = async () => {
    setShowLoading(true);

    try {
      // Converte para número inteiro ao salvar
      const percentualNumerico = parseInt(percentualCashback, 10);

      const objectRequest = {
        cashbackAtivado,
        percentualCashback: percentualNumerico.toString(),
        prazoCashback
      };

      const id = loja;
      const url = empresaAlterar.url
        .replace("{id}", id.replaceAll("#", "%23"))
        .concat("?tipo=cashback");

      await requestService(
        url,
        {
          method: empresaAlterar.method,
          body: JSON.stringify(objectRequest),
          headers: [["token", window.localStorage.getItem("token")]],
          credentials: 'include'
        },
        history,
        (response: any) => {
          if (response) {
            setTextToast("Configurações de cashback salvas com sucesso!");
            setShowSucesso(true);
          } else {
            exibirToast("Ocorreu um erro ao salvar as configurações.");
          }
          setShowLoading(false);
        },
        (error: any) => {
          console.error('Erro ao salvar configurações:', error);
          exibirToast("Ocorreu um erro ao salvar as configurações.");
          setShowLoading(false);
        }
      );
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      exibirToast("Ocorreu um erro ao salvar as configurações.");
      setShowLoading(false);
    }
  };

  // Funções auxiliares
  const validarESalvar = () => {
    if (!validarPercentual(percentualCashback)) {
      exibirToast(`Por favor, insira um percentual válido entre ${MIN_PERCENTUAL} e ${MAX_PERCENTUAL}.`);
      return;
    }

    setShowConfirmAlert(true);
  };

  const exibirToast = (mensagem: string) => {
    setTextToast(mensagem);
    setShowToast(true);
  };

  const podeEditar = acessos?.configuracoes === 'edicao';

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon slot="icon-only" icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Cashback</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Descrição */}
        <div style={{
          padding: '20px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.5'
          }}>
            Aumente suas vendas e fidelize seus clientes oferecendo créditos para que façam novos pedidos.
            Configure o Cashback de acordo com a melhor estratégia para seu estabelecimento.
          </p>
        </div>

        {/* Card Cashback */}
        <div style={{
          margin: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {/* Header do card com toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #f0f0f0',
            gap: '16px'
          }}>
            <div style={{
              flex: 1,
              minWidth: 0
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Cashback
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '13px',
                color: '#666'
              }}>
                O jeito mais fácil de fidelizar seus clientes com um percentual em cima do valor do pedido
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              <IonToggle
                checked={cashbackAtivado}
                onIonChange={(e) => setCashbackAtivado(e.detail.checked)}
                disabled={!podeEditar}
              />
            </div>
          </div>

          {/* Campo Percentual */}
          <div style={{ padding: '16px 20px 20px 20px' }}>
            <IonLabel position="stacked">
              <strong>Percentual*</strong>
            </IonLabel>
            <div style={{
              position: 'relative',
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <IonInput
                type="text"
                value={percentualCashback}
                placeholder="5"
                maxlength={2}
                disabled={!cashbackAtivado || !podeEditar}
                onIonInput={handlePercentualChange}
                onIonBlur={handlePercentualBlur}
                style={{
                  '--background': cashbackAtivado ? '#ffffff' : '#f5f5f5',
                  '--padding-start': '12px',
                  '--padding-end': '40px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  flex: 1
                }}
              />
              <span style={{
                position: 'absolute',
                right: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
                fontSize: '16px',
                fontWeight: 'normal',
                pointerEvents: 'none',
                zIndex: 10,
                userSelect: 'none'
              }}>
                %
              </span>
            </div>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '12px',
              color: '#999'
            }}>
              Valor entre {MIN_PERCENTUAL + 1} e {MAX_PERCENTUAL}%
            </p>

            {/* Campo Prazo de expiração */}
            <div style={{ marginTop: '24px' }}>
              <IonLabel position="stacked">
                <strong>Prazo de expiração</strong>
              </IonLabel>
              <IonRadioGroup
                value={prazoCashback}
                onIonChange={(e) => setPrazoCashback(e.detail.value)}
                style={{
                  marginTop: '12px',
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}
              >
                {[15, 30, 45, 60].map((dias) => (
                  <label
                    key={dias}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: cashbackAtivado && podeEditar ? 'pointer' : 'not-allowed',
                      opacity: cashbackAtivado && podeEditar ? 1 : 0.5,
                      gap: '8px'
                    }}
                  >
                    <IonRadio
                      value={dias}
                      disabled={!cashbackAtivado || !podeEditar}
                      color="primary"
                      style={{
                        cursor: cashbackAtivado && podeEditar ? 'pointer' : 'not-allowed'
                      }}
                    />
                    <span style={{
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      {dias} dias
                    </span>
                  </label>
                ))}
              </IonRadioGroup>
            </div>
          </div>
        </div>

        {/* Card Regras de uso */}
        <div style={{
          margin: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '20px' }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Regras de uso
            </h3>

            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              color: '#666',
              lineHeight: '1.8'
            }}>
              <li>
                <strong>Como resgatar créditos acumulados?</strong>
                <br />
                Ao finalizar o pedido, seu cliente seleciona a opção "Adicionar Cashback" e o saldo será descontado do valor total do pedido. Só é possível resgatar o valor total do saldo disponível.
              </li>
              <li style={{ marginTop: '12px' }}>
                <strong>Como é calculado o valor do cashback?</strong>
                <br />
                O cashback é concedido para pedidos realizados e é calculado em cima do valor do pedido.
              </li>
              <li style={{ marginTop: '12px' }}>
                <strong>O que acontece se eu desativar o Cashback?</strong>
                <br />
                Se você desativar o Cashback, o cliente não pode mais resgatar o saldo acumulado.
              </li>
            </ul>
          </div>
        </div>

        {/* Toasts e Alerts */}
        <IonToast
          isOpen={showToast}
          position="middle"
          onDidDismiss={() => setShowToast(false)}
          message={textToast}
          duration={3000}
        />

        <IonAlert
          isOpen={showConfirmAlert}
          onDidDismiss={() => setShowConfirmAlert(false)}
          header={'Salvar configurações'}
          message={'Você <strong>confirma</strong> as alterações nas configurações de cashback?'}
          buttons={[
            {
              text: 'Cancelar',
              role: 'cancel',
            },
            {
              text: 'Confirmar',
              cssClass: 'primary',
              handler: () => {
                salvarConfiguracoes();
              }
            }
          ]}
        />

        <IonLoading
          cssClass='my-custom-class'
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          spinner='circles'
        />

        <IonLoading
          cssClass='my-custom-class'
          isOpen={showSucesso}
          onDidDismiss={() => { setShowSucesso(false); history.goBack(); }}
          message={'Configurações salvas com sucesso!'}
          duration={3000}
          animated={false}
        />
      </IonContent>

      <IonFooter className="ion-no-border">
        <IonToolbar>
          <IonButton
            onClick={validarESalvar}
            expand="block"
            type="submit"
            disabled={!podeEditar}
          >
            Salvar
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Cashback;